/**
 * HoldStations — scene-scoped registry of "stand here on purpose" zones.
 *
 * The solo-mode companion follow (see SoloFollow.ts) walks the idle cat
 * toward the active cat whenever they separate beyond the trigger
 * distance. That is the right default for traversal, but it silently
 * breaks every mechanic built on parking a cat somewhere: pressure
 * plates, dual-entry portals, sync pads. The player deliberately places
 * cat A, switches to cat B, walks away — and the follow logic marches
 * cat A right off its post.
 *
 * Entities that represent a deliberate parking spot register a station
 * here (PressurePlate while pressed, Portal while open). Each frame the
 * BaseLevel solo loop asks whether the idle cat is inside any station
 * and, if so, suppresses follow/warp for that frame.
 *
 * The registry is scene-scoped (WeakMap) so leaked registrations cannot
 * outlive their scene, and `register` returns an unsubscribe for prompt
 * cleanup on entity dispose.
 */
import type { Scene } from "@babylonjs/core/scene";
import type { Vector3 } from "@babylonjs/core/Maths/math.vector";

export interface HoldStation {
  /** Debug/QA identifier (usually the owning interactive's key). */
  key: string;
  /**
   * Whether `pos` (a cat capsule center) currently counts as "parked on
   * this station". Implementations should return false while the station
   * is inert (unpressed plate, sealed portal) so cats aren't glued to
   * scenery that isn't doing anything yet.
   */
  contains(pos: Vector3): boolean;
}

const registries = new WeakMap<Scene, Set<HoldStation>>();

/** Register a hold station for the scene. Returns an unregister function. */
export function registerHoldStation(scene: Scene, station: HoldStation): () => void {
  let set = registries.get(scene);
  if (!set) {
    set = new Set();
    registries.set(scene, set);
  }
  set.add(station);
  return () => {
    registries.get(scene)?.delete(station);
  };
}

/** True when `pos` is inside any registered, active hold station. */
export function isOnHoldStation(scene: Scene, pos: Vector3): boolean {
  const set = registries.get(scene);
  if (!set) return false;
  for (const station of set) {
    if (station.contains(pos)) return true;
  }
  return false;
}
